import { forwardRef, ReactNode } from "react";

import { Theme, SxProps, Tooltip, TooltipProps, useTheme, BoxProps } from "@mui/material";

import { mergeSx } from "@/common/helpers/mergeSx";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

export type FormTooltipProps = Omit<TooltipProps, "arrow" | "slotProps" | "components" | "placement" | "title"> & {
  title: ReactNode;
  maxWidth?: BoxProps["maxWidth"];
  placement?: "top-start" | "top" | "top-end" | "left" | "right" | "bottom-start" | "bottom" | "bottom-end";
  components?: {
    tooltip?: {
      sx?: SxProps<Theme>;
    };
    arrow?: {
      sx?: SxProps<Theme>;
    };
  };
};

export const FormTooltip = forwardRef<HTMLDivElement, FormTooltipProps>(
  (
    { title, maxWidth = "200px", children, placement = "top", components, enterTouchDelay, leaveTouchDelay, ...rest },
    ref
  ) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.tooltip) {
      return <InvalidThemeErrorMessage />;
    }

    const tooltipStyles = mergeSx(
      {
        ...theme.typography.caption,
        maxWidth,
        padding: theme.spacing(0.75, 1),
        textAlign: "left",
        color: theme.palette.formsComponents?.tooltip?.textColor,
        backgroundColor: theme.palette.formsComponents?.tooltip?.backgroundColor,
        borderRadius: theme.radius(1),
        boxShadow: theme.shadows[8],
        ...((placement === "top-start" || placement === "bottom-start") && {
          left: "-16px",
        }),
        ...((placement === "top-end" || placement === "bottom-end") && {
          left: "16px",
        }),
      },
      components?.tooltip?.sx
    );

    const arrowStyles = mergeSx(
      {
        color: theme.palette.formsComponents?.tooltip?.backgroundColor,
        // Material doesn't allow us to override the tooltip arrow position, so we have to put !important
        ...((placement === "top-start" || placement === "bottom-start") && {
          marginLeft: "16px",
        }),
        ...((placement === "top-end" || placement === "bottom-end") && {
          marginLeft: "-16px",
        }),
      },
      components?.arrow?.sx
    );

    return (
      <Tooltip
        ref={ref}
        placement={placement}
        title={title}
        arrow
        slotProps={{ tooltip: { sx: tooltipStyles }, arrow: { sx: arrowStyles } }}
        enterTouchDelay={enterTouchDelay ?? 0}
        leaveTouchDelay={leaveTouchDelay ?? 10000}
        {...rest}
      >
        {children}
      </Tooltip>
    );
  }
);

FormTooltip.displayName = "FormTooltip";
